import CryptoJS from "crypto-js";

export const GUEST_ID_KEY = "gx_92kQ";
export const GUEST_CART_KEY = "cRt_7dP1";
export const CART_SYNC_KEY = "sYnC_4LrX";
export const CART_HASH_KEY = "cRt_hash";
export const CART_QUANTITIES_KEY = "cRt_quantities";

const SECRET = "cRt_!@#_local"; // local-only hash salt, not sensitive

export const supportsHalfSlot = (...values) => values.some((value) =>
    value === true || value === 1 || String(value).trim().toLowerCase() === "true" || String(value).trim() === "1"
);

const isExpired = (item) => {
    const endDate = item?.join_period_end_date;
    if (!endDate) return false;
    const parsed = new Date(endDate).getTime();
    if (Number.isNaN(parsed)) return false;
    return parsed < Date.now();
};

const validateCartItem = (item) => {
    if (!item || typeof item !== "object") return false;

    const hasValidId = typeof item._id === "string" && item._id.trim() !== "";
    const hasValidSplitBuyId = typeof item.split_buy_id === "string" && item.split_buy_id.trim() !== "";
    const hasValidPrice = typeof item.price === "number" && item.price >= 0;
    const hasValidAvailableSlots = typeof item.available_slots === "number" && item.available_slots >= 0;

    return hasValidId && hasValidSplitBuyId && hasValidPrice && hasValidAvailableSlots;
};

const computeHash = (data) => CryptoJS.SHA256(JSON.stringify(data) + SECRET).toString();

export const CartStorageUtil = {
    getCartQuantities() {
        try {
            const stored = sessionStorage.getItem(CART_QUANTITIES_KEY);
            const parsed = stored ? JSON.parse(stored) : {};

            if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") return {};

            return Object.fromEntries(
                Object.entries(parsed).filter(([splitBuyId, quantity]) =>
                    splitBuyId && Number.isFinite(Number(quantity)) && Number(quantity) > 0
                )
            );
        } catch {
            return {};
        }
    },

    getCartQuantity(splitBuyId) {
        if (!splitBuyId) return undefined;
        const quantity = Number(this.getCartQuantities()[splitBuyId]);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : undefined;
    },

    setCartQuantity(splitBuyId, quantity) {
        const normalizedQuantity = Number(quantity);
        if (!splitBuyId || !Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) return;

        sessionStorage.setItem(CART_QUANTITIES_KEY, JSON.stringify({
            ...this.getCartQuantities(),
            [splitBuyId]: normalizedQuantity,
        }));
    },

    removeCartQuantity(splitBuyId) {
        if (!splitBuyId) return;
        const quantities = this.getCartQuantities();
        delete quantities[splitBuyId];
        sessionStorage.setItem(CART_QUANTITIES_KEY, JSON.stringify(quantities));
    },

    clearCartQuantities() {
        sessionStorage.removeItem(CART_QUANTITIES_KEY);
    },

    getGuestId() {
        return localStorage.getItem(GUEST_ID_KEY);
    },

    setGuestId(id) {
        localStorage.setItem(GUEST_ID_KEY, id);
    },

    getGuestCart() {
        try {
            const stored = localStorage.getItem(GUEST_CART_KEY);
            const hash = localStorage.getItem(CART_HASH_KEY);
            if (!stored) return [];

            const parsed = JSON.parse(stored);
            if (!Array.isArray(parsed)) return [];

            const valid = computeHash(parsed) === hash;
            if (!valid) {
                this.clearGuestCart();
                return [];
            }

            const validItems = parsed.filter(validateCartItem);
            const nonExpiredItems = validItems.filter((item) => !isExpired(item));
 
            if (nonExpiredItems.length !== validItems.length) {
                this.setGuestCart(nonExpiredItems);
            }
            return nonExpiredItems;

        } catch {
            return [];
        }
    },

    setGuestCart(cart) {
        const cleanCart = cart.filter(validateCartItem);
        const json = JSON.stringify(cleanCart);
        localStorage.setItem(GUEST_CART_KEY, json);
        localStorage.setItem(CART_HASH_KEY, computeHash(cleanCart));
    },

    clearGuestCart() {
        localStorage.removeItem(GUEST_CART_KEY);
        localStorage.removeItem(CART_HASH_KEY);
    },

    clearGuestId() {
        localStorage.removeItem(GUEST_ID_KEY);
    },
    setLastSynced() {
        localStorage.setItem(CART_SYNC_KEY, Date.now().toString());
    }, getLastSynced() {
        return Number(localStorage.getItem(CART_SYNC_KEY)) || 0;
    }, clearLastSynced() {
        localStorage.removeItem(CART_SYNC_KEY);
    }, mapCartItem(item) {
        const splitBuy = item?.split_buy;
        const cartItemId = item?._id || item?.id;
        const splitBuyId = splitBuy?._id || splitBuy?.id;

        return {
            _id: cartItemId,
            split_buy_id: splitBuyId,
            split_buy_slug: splitBuy?.slug,
            image: splitBuy?.product?.picture_urls?.[0] || "",
            title: splitBuy?.product?.title || "Unknown Product",
            location: Array.isArray(splitBuy?.choice_of_delivery_locations) && splitBuy.choice_of_delivery_locations.length
              ? splitBuy.choice_of_delivery_locations.join(", ")
              : splitBuy?.split_buy_product_location?.string || "Location not specified",
            choice_of_delivery_locations: splitBuy?.choice_of_delivery_locations || [],
            delivery_date: splitBuy?.delivery_date || splitBuy?.join_period_end_date,
            price: splitBuy?.split_cost_per_slot || 0,
            available_slots: splitBuy?.fractional_slots_left ?? ((splitBuy?.number_of_buy_ins_required || 0) - (splitBuy?.fractional_number_of_buy_ins_attained || 0)),
            allow_half_slot: supportsHalfSlot(
                item?.allow_half_slot,
                splitBuy?.allow_half_slot,
                item?.half_slot_supported,
                splitBuy?.half_slot_supported
            ),
            slot: this.getCartQuantity(splitBuyId) ?? item?.slot ?? item?.quantity ?? 1,
            fractional_number_of_buy_ins_attained:	splitBuy?.fractional_number_of_buy_ins_attained || 0,
            join_period_end_date: splitBuy?.join_period_end_date || null,
        };
    }, normalizeCartResponse(items) {
        return items.map(item => CartStorageUtil.mapCartItem(item));
    },

    normalizeCartItem(item) {
        return CartStorageUtil.mapCartItem(item);
    }

};
